// components/InstallPWA.jsx
import { useEffect, useState, useCallback, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Storage key for tracking if user has dismissed the prompt
const PROMPT_DISMISSED_KEY = 'pwa_install_prompt_dismissed';

export default function InstallPWA() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const eventFiredRef = useRef(false);

  // Check if PWA is already installed
  const checkIfPWAInstalled = useCallback(async () => {
    // For iOS
    if (window.navigator.standalone) {
      return true;
    }
    // For Android/Chrome
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    // For Windows
    if (window.navigator.userAgent.includes('Windows') && window.navigator.getInstalledRelatedApps) {
      try {
        const apps = await window.navigator.getInstalledRelatedApps();
        return apps.length > 0;
      } catch (error) {
        return false;
      }
    }
    return false;
  }, []);

  // Handle installation
  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }
    
    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      // If the user accepted, we don't need to show the prompt again
      if (outcome === 'accepted') {
        // Mark as installed in local storage
        localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
        setShowInstallPrompt(false);
        setIsInstalled(true);
      } else {
        // User dismissed, don't show again for 7 days
        localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
        setShowInstallPrompt(false);
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
    } catch (error) {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  }, [deferredPrompt]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    // Store that the user dismissed the prompt (don't show for 7 days)
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    setShowInstallPrompt(false);
    // Clear the deferred prompt if it exists
    if (deferredPrompt) {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  useEffect(() => {
    // Check if already installed
    checkIfPWAInstalled().then(installed => {
      setIsInstalled(installed);
      if (installed) {
        localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
      }
    });

    // Check if the browser supports the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      eventFiredRef.current = true;
      
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      
      // Check if user has already dismissed the prompt
      const hasDismissed = localStorage.getItem(PROMPT_DISMISSED_KEY) === 'true';
      
      if (!hasDismissed && !isInstalled) {
        // Stash the event so it can be triggered later
        setDeferredPrompt(e);
        setShowInstallPrompt(true);
      }
    };

    // Check if the app is already installed
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone || 
          document.referrer.includes('android-app://')) {
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkInstalled()) {
      setIsInstalled(true);
      localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    }

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
      setDeferredPrompt(null);
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if event already fired (if component mounts after event)
    // Some browsers fire the event immediately on page load
    if (window.deferredInstallPrompt) {
      handleBeforeInstallPrompt(window.deferredInstallPrompt);
    }

    // Clean up
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [checkIfPWAInstalled, isInstalled]);

  // Don't show if already installed or if prompt is dismissed
  if (isInstalled || !showInstallPrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-xs z-50 border border-gray-200 animate-in slide-in-from-bottom-4">
      <div className="flex items-start">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900">Install PLSSS Agent App</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add PLSSS Agent to your home screen for faster access and offline use.
          </p>
          <div className="mt-3 flex space-x-3">
            <button
              type="button"
              onClick={handleInstallClick}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Install
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Not Now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-500 ml-2"
          aria-label="Dismiss installation prompt"
        >
          <span className="sr-only">Dismiss</span>
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
